/**
 * services/BasicSettingsService.ts
 * Basic application settings management service
 *
 * Provides singleton settings for core application configuration,
 * including user-defined timeframe options for trading charts.
 */

import { BaseService } from './BaseService';
import type { BasicSettings, TimeframeOption } from '@/types/schema/BasicSettings';
import { DEFAULT_BASIC_SETTINGS } from '@/config/defaults';
import { sortTimeframeOptions } from '@/utils';

/**
 * Service for managing basic application settings
 *
 * Extends BaseService to provide standard CRUD operations plus
 * specialized methods for getting or creating default settings
 * and managing timeframe options.
 */
export class BasicSettingsService extends BaseService<BasicSettings> {
  constructor() {
    super('basic_settings');
  }

  /**
   * Get default basic settings
   *
   * @returns Default settings object (without id, timestamps, or sync fields)
   * @private
   */
  private getDefaultSettings(): Omit<
    BasicSettings,
    'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'deletedByDevice'
  > {
    return DEFAULT_BASIC_SETTINGS;
  }

  /**
   * Get existing settings or create default settings if none exist
   *
   * This is the primary method for retrieving basic settings.
   * On first use, it creates a default settings record that the
   * user can then customize.
   *
   * Also ensures timeframeOptions is populated with defaults if empty
   * (handles users created via onboarding before Phase 2 defaults existed).
   *
   * @returns Promise resolving to BasicSettings
   */
  async getOrCreateDefaultSettings(): Promise<BasicSettings> {
    try {
      // Check if settings already exist (getAll excludes tombstones)
      const allSettings = await this.getAll();

      let settings: BasicSettings;

      if (allSettings.length > 0) {
        // Use first settings record (singleton semantics)
        const firstSetting = allSettings[0];
        if (!firstSetting) {
          throw new Error('Unexpected undefined settings record');
        }
        settings = firstSetting;
      } else {
        // No settings exist, create default
        const defaultSettings = this.getDefaultSettings();
        settings = await this.add(defaultSettings);
      }

      // Ensure timeframeOptions is populated (handles empty arrays from onboarding)
      if (!settings.timeframeOptions?.length) {
        settings = await this.update(settings.id, {
          timeframeOptions: DEFAULT_BASIC_SETTINGS.timeframeOptions
        });
      }

      return settings;
    } catch (error) {
      console.error('Failed to get or create basic settings:', error);
      throw new Error('Failed to initialize basic settings');
    }
  }

  /**
   * Update the list of timeframe options
   *
   * Validates, deduplicates, and sorts options before saving:
   * - Normalizes interval to integer via Math.floor()
   * - Rejects entries with interval <= 0
   * - Deduplicates by (category, interval) tuple, first occurrence wins
   * - Sorts by category priority then interval ascending
   *
   * @param options - Array of TimeframeOption objects
   * @returns Promise resolving to updated BasicSettings
   */
  async updateTimeframeOptions(options: TimeframeOption[]): Promise<BasicSettings> {
    // Validate and normalize intervals
    const validOptions = options
      .map(opt => ({
        ...opt,
        interval: Math.floor(opt.interval)
      }))
      .filter(opt => opt.interval > 0);

    // Deduplicate by (category, interval) tuple, first occurrence wins
    const seen = new Set<string>();
    const uniqueOptions = validOptions.filter(opt => {
      const key = `${opt.category}:${opt.interval}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    // Sort by category priority then interval ascending
    const sortedOptions = sortTimeframeOptions(uniqueOptions);

    // Get current settings (creates if needed)
    const currentSettings = await this.getOrCreateDefaultSettings();

    // Update with sorted options
    const updatedSettings = await this.update(currentSettings.id, {
      timeframeOptions: sortedOptions
    });

    return updatedSettings;
  }
}

// Export singleton instance for use throughout the application
export const basicSettingsService = new BasicSettingsService();
